import React, { useState } from "react";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import {
  useUpdateElementData,
  useElementEnter,
  useElementBackspaceStart,
  useAutoFocus,
  useElementArrow,
  useElementIndent,
  useElementPasteElements,
  useElementShiftArrow,
  useSlashCommandTypes,
  useElementConvert,
  useRegisterRichTextEditor,
  useElementVirtualSelection,
} from "../DocumentView/DocumentContext";
import { usePageCollab } from "../DocumentView/PageCollabContext";
import PageRichText from "./PageRichText";
import { cn } from "../lib/utils";

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

interface Props {
  element: DocumentElementType;
}

// Warm-neutral heading scale (size / weight / line-height / tracking / ink)
// from the Modern Editor design tokens.
const LEVEL_CLASS: Record<number, string> = {
  1: "text-[32px] font-extrabold leading-[1.18] tracking-[-0.021em] text-[#1f1d1a]",
  2: "text-[24px] font-bold leading-[1.22] tracking-[-0.015em] text-[#22201c]",
  3: "text-[19px] font-bold leading-[1.28] tracking-[-0.01em] text-[#2a2723]",
  4: "text-[16px] font-bold leading-[1.3] text-[#33302b]",
};

const extractLevel = (raw: unknown): 1 | 2 | 3 | 4 => {
  const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
  return n === 1 || n === 3 || n === 4 ? n : 2;
};

/**
 * A page-document heading. There are four fixed heading sizes — the block TYPE
 * (`page-heading-1` … `page-heading-4`) is the size (Notion-style), with a
 * fallback to `data.level` for any legacy `page-heading` content.
 *
 * A heading is just a text block with fixed typography and tighter rules, so
 * editing runs on the shared PageRichText surface in `plain` + `singleLine`
 * mode: no marks/toolbar/links/colours/mentions, no soft breaks, pastes
 * flatten to one unformatted line. That buys headings the same live
 * collaboration as text blocks — per-character merge, remote carets, block
 * presence — with the plain string mirrored into `data.text` for the
 * read-model. Typography sits on the wrapper (inherited by the
 * contenteditable), so a level change restyles without recreating the editor.
 * Without an authoring write-path it renders as a static <h*>.
 */
const PageHeading: React.FC<Props> = ({ element }) => {
  const data = element.data as { text?: string; level?: unknown };
  const levelFromType = /page-heading-(\d)/.exec(element.typeId)?.[1];
  const level = extractLevel(levelFromType ?? data?.level);
  const text = typeof data?.text === "string" ? data.text : "";
  const update = useUpdateElementData(element.id);
  const enter = useElementEnter(element.id);
  const backspaceStart = useElementBackspaceStart(element.id);
  const arrow = useElementArrow(element.id);
  const indent = useElementIndent(element.id);
  const pasteElements = useElementPasteElements(element.id);
  const shiftArrow = useElementShiftArrow(element.id);
  const { shouldFocus, caret, coords, consume } = useAutoFocus(element.id);
  const slashTypes = useSlashCommandTypes();
  const convert = useElementConvert(element.id);
  const registerEditor = useRegisterRichTextEditor(element.id);
  const virtualSelection = useElementVirtualSelection(element.id);
  const pageCollab = usePageCollab();
  const collabFragment = pageCollab?.getContentFragment(element.id) ?? null;
  // Placeholder visibility — fed by the editor (covers remote edits too).
  const [empty, setEmpty] = useState(text === "");

  // `React.ElementType` (not `keyof JSX.IntrinsicElements`) — the latter's prop
  // union is too complex for tsc once extra props are spread on.
  const Tag = `h${level}` as React.ElementType;
  const typography = cn("font-jakarta", LEVEL_CLASS[level]);

  if (!update) {
    return <Tag className={typography}>{text}</Tag>;
  }

  return (
    <div className="relative">
      <PageRichText
        value={`<p>${escapeHtml(text)}</p>`}
        editable
        plain
        singleLine
        className={cn(typography, "[&_p]:m-0")}
        onChange={(_html, plainText) =>
          update((d) => {
            d.text = plainText ?? "";
          })
        }
        onEmptyChange={setEmpty}
        autoFocus={shouldFocus ? { caret, coords } : null}
        onAutoFocused={consume}
        onEnter={
          enter
            ? (isEmpty, afterHtml, atStart) =>
                enter({ isEmpty, afterHtml, atStart })
            : undefined
        }
        onBackspaceAtStart={backspaceStart ?? undefined}
        slashTypes={slashTypes ?? undefined}
        onConvert={convert ?? undefined}
        onArrow={arrow ?? undefined}
        onIndent={indent ?? undefined}
        onPasteElements={pasteElements ?? undefined}
        onShiftArrow={shiftArrow ?? undefined}
        onEditorHandle={registerEditor}
        virtualSelection={virtualSelection}
        collab={
          pageCollab && collabFragment
            ? {
                fragment: collabFragment,
                provider: pageCollab.provider,
                user: pageCollab.user,
                onFocusChange: pageCollab.setEditingBlockId
                  ? (focused: boolean) =>
                      pageCollab.setEditingBlockId?.(
                        focused ? element.id : null,
                      )
                  : undefined,
              }
            : null
        }
      />
      {empty && (
        <span
          aria-hidden
          className={cn(
            typography,
            "pointer-events-none absolute left-0 top-0 select-none text-[var(--forms-muted,#a5a199)]",
          )}
        >
          Heading
        </span>
      )}
    </div>
  );
};

export default PageHeading;
